/**
 * Athletes Launch — Idle Session Guard
 *
 * Monitors user activity and automatically signs out inactive sessions
 * to protect athletes' personal data, financial info, and contracts.
 *
 * Timeout schedule:
 *   30 minutes idle → fire onWarn callback (show warning modal)
 *   35 minutes idle → fire onExpire callback (force sign-out)
 *
 * Usage:
 *   const guard = createSessionGuard({ onWarn, onExpire });
 *   guard.start();
 *   // On component unmount or logout:
 *   guard.stop();
 */

const WARN_AFTER_MS   = 30 * 60 * 1000; // 30 minutes
const EXPIRE_AFTER_MS = 35 * 60 * 1000; // 35 minutes

/** DOM events that count as user activity */
const ACTIVITY_EVENTS = [
  'mousemove', 'mousedown', 'keydown',
  'touchstart', 'scroll', 'click', 'focus',
];

/**
 * Create a session guard instance.
 *
 * @param {Object}   options
 * @param {Function} options.onWarn    Called when idle for 30 minutes
 * @param {Function} options.onExpire  Called when idle for 35 minutes (sign-out)
 * @returns {{ start: Function, stop: Function, resetTimer: Function }}
 */
export function createSessionGuard({ onWarn, onExpire }) {
  let warnTimer   = null;
  let expireTimer = null;
  let warned      = false;

  function clearAllTimers() {
    clearTimeout(warnTimer);
    clearTimeout(expireTimer);
  }

  function resetTimers() {
    clearAllTimers();
    warned = false;

    warnTimer = setTimeout(() => {
      warned = true;
      if (typeof onWarn === 'function') onWarn();
    }, WARN_AFTER_MS);

    expireTimer = setTimeout(() => {
      if (typeof onExpire === 'function') onExpire();
    }, EXPIRE_AFTER_MS);
  }

  function handleActivity() {
    // Once warned, don't reset — the modal handles "stay signed in"
    if (!warned) {
      resetTimers();
    }
  }

  /** Start monitoring activity and arm the timers. */
  function start() {
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );
    resetTimers();
  }

  /** Stop monitoring and cancel all pending timers. */
  function stop() {
    ACTIVITY_EVENTS.forEach((evt) =>
      window.removeEventListener(evt, handleActivity)
    );
    clearAllTimers();
  }

  return { start, stop, resetTimer: resetTimers };
}
